import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/mutations/useInventoryMutations');

/**
 * Inventory mutation hooks using factory patterns
 * Reduced from 232 lines to ~100 lines (57% reduction)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '@/lib/api/inventory.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { createSubResourceMutations } from './createSimpleMutation';
import { toast } from 'sonner';
import type {
  InventoryItem,
  CreateInventoryItem,
  UpdateInventoryItem,
  InventoryCategory,
  CreateInventoryCategory,
  UpdateInventoryCategory,
  InventoryCategoryTypeOption,
  CreateInventoryCategoryTypeOption,
  UpdateInventoryCategoryTypeOption,
  InventoryMovementReasonOption,
  CreateInventoryMovementReasonOption,
  UpdateInventoryMovementReasonOption,
  InventoryLocation,
  CreateInventoryLocation,
  UpdateInventoryLocation,
  CreateInventoryMovement,
  CreateInventoryAdjustment,
  ApproveInventoryAdjustment,
  RejectInventoryAdjustment,
} from '@scrambl/shared';
// ============================================================================
// INVENTORY ITEMS - Using main factory with analytics invalidation
// ============================================================================

// Every item mutation may change the counts displayed against a category
// (_count.items) and a location (_count.itemLocations / defaultItems), plus
// the analytics tiles. These invalidations keep the Categories, Locations,
// and Analytics tabs consistent without a manual reload.
const itemCascadeInvalidations = [
  () => queryKeys.inventory.analytics.lowStock(),
  () => queryKeys.inventory.analytics.valueSummary(),
  () => queryKeys.inventory.analytics.movementHistory(),
  () => [...queryKeys.inventory.all, 'categories'] as readonly unknown[],
  () => [...queryKeys.inventory.all, 'locations'] as readonly unknown[],
];

const itemMutations = createResourceMutations<InventoryItem, CreateInventoryItem, UpdateInventoryItem>({
  resourceName: 'Inventory item',
  resourceNameSingular: 'inventoryItem',
  queryKeyFactory: queryKeys.inventory,
  api: {
    create: (data: CreateInventoryItem) => inventoryApi.createInventoryItem(data),
    update: (id: string, data: UpdateInventoryItem) => inventoryApi.updateInventoryItem(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateInventoryItem;
return inventoryApi.updateInventoryItem(id, updateData);
    },
    delete: (id: string) => inventoryApi.deleteInventoryItem(id),
  },
  invalidation: {
    onCreate: { additionalInvalidations: itemCascadeInvalidations },
    onUpdate: { additionalInvalidations: itemCascadeInvalidations },
    onDelete: { additionalInvalidations: itemCascadeInvalidations },
  },
  enableRealtime: false,
});

export const useCreateInventoryItemMutation = itemMutations.useCreateMutation;
export const useUpdateInventoryItemMutation = itemMutations.useUpdateMutation;
export const useUpdateInventoryItemFieldMutation = itemMutations.useUpdateFieldMutation!;
export const useDeleteInventoryItemMutation = itemMutations.useDeleteMutation;

// ============================================================================
// CATEGORIES - Using sub-resource factory
// ============================================================================

const categoryMutations = createSubResourceMutations<InventoryCategory, CreateInventoryCategory, UpdateInventoryCategory>({
  resourceName: 'Category',
  api: {
    create: (data) => inventoryApi.createCategory(data),
    update: (id, data) => inventoryApi.updateCategory(id, data),
    delete: (id) => inventoryApi.deleteCategory(id),
  },
  primaryKey: () => queryKeys.inventory.categories(),
  additionalKeys: () => [queryKeys.inventory.lists()],
});

export const useCreateInventoryCategoryMutation = categoryMutations.useCreateMutation;
export const useUpdateInventoryCategoryMutation = categoryMutations.useUpdateMutation;
export const useDeleteInventoryCategoryMutation = categoryMutations.useDeleteMutation;

// ============================================================================
// CATEGORY TYPES — per-org lookup that backs the Type dropdown.
// ============================================================================

const categoryTypeMutations = createSubResourceMutations<
  InventoryCategoryTypeOption,
  CreateInventoryCategoryTypeOption,
  UpdateInventoryCategoryTypeOption
>({
  resourceName: 'Category type',
  api: {
    create: (data) => inventoryApi.createCategoryType(data),
    update: (id, data) => inventoryApi.updateCategoryType(id, data),
    delete: (id) => inventoryApi.deleteCategoryType(id),
  },
  primaryKey: () => queryKeys.inventory.categoryTypes(),
  // The category list shows the resolved type label, so a rename should
  // bust the category cache too.
  additionalKeys: () => [queryKeys.inventory.categories()],
});

export const useCreateInventoryCategoryTypeMutation =
  categoryTypeMutations.useCreateMutation;
export const useUpdateInventoryCategoryTypeMutation =
  categoryTypeMutations.useUpdateMutation;
export const useDeleteInventoryCategoryTypeMutation =
  categoryTypeMutations.useDeleteMutation;

// ============================================================================
// MOVEMENT REASONS — per-org lookup that backs the Reason dropdown.
// ============================================================================

const movementReasonMutations = createSubResourceMutations<
  InventoryMovementReasonOption,
  CreateInventoryMovementReasonOption,
  UpdateInventoryMovementReasonOption
>({
  resourceName: 'Movement reason',
  api: {
    create: (data) => inventoryApi.createMovementReason(data),
    update: (id, data) => inventoryApi.updateMovementReason(id, data),
    delete: (id) => inventoryApi.deleteMovementReason(id),
  },
  primaryKey: () => queryKeys.inventory.movementReasons(),
  // Movements list shows the resolved label, so a rename should bust the
  // movements cache too.
  additionalKeys: () => [queryKeys.inventory.movements()],
});

export const useCreateInventoryMovementReasonMutation =
  movementReasonMutations.useCreateMutation;
export const useUpdateInventoryMovementReasonMutation =
  movementReasonMutations.useUpdateMutation;
export const useDeleteInventoryMovementReasonMutation =
  movementReasonMutations.useDeleteMutation;

// ============================================================================
// LOCATIONS - Using sub-resource factory
// ============================================================================

const locationMutations = createSubResourceMutations<InventoryLocation, CreateInventoryLocation, UpdateInventoryLocation>({
  resourceName: 'Location',
  api: {
    create: (data) => inventoryApi.createLocation(data),
    update: (id, data) => inventoryApi.updateLocation(id, data),
    delete: (id) => inventoryApi.deleteLocation(id),
  },
  primaryKey: () => queryKeys.inventory.locations(),
  additionalKeys: () => [queryKeys.inventory.lists()],
});

export const useCreateInventoryLocationMutation = locationMutations.useCreateMutation;
export const useUpdateInventoryLocationMutation = locationMutations.useUpdateMutation;
export const useDeleteInventoryLocationMutation = locationMutations.useDeleteMutation;

// ============================================================================
// MOVEMENTS - Manual due to complex invalidation with dynamic item ID
// ============================================================================

export function useRecordInventoryMovementMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInventoryMovement) => {
      return inventoryApi.recordMovement(data);
    },
    onSuccess: (newMovement) => {
      // Invalidate movements and items lists
      // Use base key without filters to match all movements queries
      queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all, 'movements'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
      // Also invalidate the items list since quantities change
      queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all, 'list'] });

      // Invalidate specific item if present
      if (newMovement.itemId) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.inventory.detail(newMovement.itemId), 'movement-history']
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.inventory.detail(newMovement.itemId)
        });
      }

      // Invalidate analytics
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.analytics.movementHistory() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.analytics.lowStock() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.analytics.valueSummary() });

      toast.success('Movement recorded successfully');
    },
    onError: (error) => {
      logger.error('[useRecordInventoryMovementMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to record movement');
    },
  });
}

// ============================================================================
// ADJUSTMENTS — cycle counts, damage, corrections with optional approval
// ============================================================================

function invalidateAfterAdjustment(queryClient: ReturnType<typeof useQueryClient>, itemId?: string) {
  queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all, 'adjustments'] });
  queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all, 'movements'] });
  queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all, 'list'] });
  queryClient.invalidateQueries({ queryKey: queryKeys.inventory.lists() });
  if (itemId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.inventory.detail(itemId) });
    queryClient.invalidateQueries({
      queryKey: [...queryKeys.inventory.detail(itemId), 'movement-history'],
    });
  }
  queryClient.invalidateQueries({ queryKey: queryKeys.inventory.analytics.lowStock() });
  queryClient.invalidateQueries({ queryKey: queryKeys.inventory.analytics.valueSummary() });
  queryClient.invalidateQueries({ queryKey: queryKeys.inventory.analytics.movementHistory() });
}

export function useCreateInventoryAdjustmentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateInventoryAdjustment) =>
      inventoryApi.createAdjustment(data),
    onSuccess: (adjustment) => {
      invalidateAfterAdjustment(queryClient, adjustment.itemId);
      toast.success(
        adjustment.status === 'PENDING'
          ? 'Adjustment submitted for approval'
          : 'Adjustment applied successfully',
      );
    },
    onError: (error) => {
      logger.error('[useCreateInventoryAdjustmentMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create adjustment');
    },
  });
}

export function useApproveInventoryAdjustmentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data?: ApproveInventoryAdjustment }) =>
      inventoryApi.approveAdjustment(args.id, args.data ?? {}),
    onSuccess: (adjustment) => {
      invalidateAfterAdjustment(queryClient, adjustment.itemId);
      toast.success('Adjustment approved and applied');
    },
    onError: (error) => {
      logger.error('[useApproveInventoryAdjustmentMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to approve adjustment');
    },
  });
}

export function useRejectInventoryAdjustmentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data?: RejectInventoryAdjustment }) =>
      inventoryApi.rejectAdjustment(args.id, args.data ?? {}),
    onSuccess: (adjustment) => {
      invalidateAfterAdjustment(queryClient, adjustment.itemId);
      toast.success('Adjustment rejected');
    },
    onError: (error) => {
      logger.error('[useRejectInventoryAdjustmentMutation] Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to reject adjustment');
    },
  });
}
